// API service - v3.0.0 with Azure AD authentication
import { getConfigSync } from './config';
import { getAccessToken } from './auth';

function getApiConfig() {
  try {
    const config = getConfigSync();
    console.log('[API] Using config:', { apiUrl: config.apiUrl, instanceId: config.instanceId });
    return {
      apiUrl: config.apiUrl,
      instanceId: config.instanceId,
    };
  } catch (error) {
    console.error('[API] Config error:', error);
    // Fallback to environment variables if config not loaded yet
    return {
      apiUrl: process.env.REACT_APP_API_URL || 'https://api.adnetapps.com',
      instanceId: process.env.REACT_APP_INSTANCE_ID,
    };
  }
}

// Create headers with Authorization token
async function getHeaders() {
  const headers = {};
  const token = await getAccessToken();
  console.log('[API] Token acquired:', token ? `${token.substring(0, 50)}...` : 'NULL');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('[API] Added Authorization header');
  } else {
    console.warn('[API] No token available - request will be unauthenticated');
  }
  return headers;
}

// Extract extension owner name from filename (e.g., "[Nia Fox]_2022-..." -> "Nia Fox")
function extractNameFromFilename(filename) {
  if (!filename) return null;
  const match = filename.match(/^\[([^\]]+)\]/);
  return match ? match[1] : null;
}

// Check if a string looks like a phone number (all digits, possibly with leading +)
function looksLikePhoneNumber(str) {
  if (!str) return true; // empty/null is not a valid name
  // eslint-disable-next-line no-useless-escape
  return /^[\d\s\-+()]+$/.test(str.trim());
}

// Map API response (v3.0.0 returns snake_case)
function mapRecording(recording) {
  const filename = recording.recording_filename || recording.filename;

  // IMPORTANT: The API's extension_name and extension_owner_name fields contain the
  // OTHER PARTY's caller ID name (e.g., "MONICA LLOYD:Business Office"), NOT the
  // extension owner. The only reliable source for the extension owner's name is
  // the filename which follows the pattern: [Owner Name]_extension-number_...
  // So we ALWAYS prefer extracting from filename first.
  let extensionName = extractNameFromFilename(filename);

  // Only fall back to API fields if filename extraction failed AND the value
  // doesn't look like caller ID data (which often has colons like "Name:Department")
  if (!extensionName) {
    const apiName = recording.extension_name || recording.extension_owner_name;
    // Skip if it looks like a phone number or caller ID with department
    if (apiName && !looksLikePhoneNumber(apiName) && !apiName.includes(':')) {
      extensionName = apiName;
    }
  }

  return {
    id: recording.id,
    extension: recording.extension,
    extensionName: extensionName,
    direction: recording.direction,
    callDateTime: recording.call_start || recording.call_time,  // irmso-processor uses call_start
    otherPartyNumber: recording.external_number || recording.source_number || recording.destination_number,
    durationSeconds: recording.duration_seconds,
    blobPath: recording.recording_s3_path || recording.blob_path,  // irmso-processor uses recording_s3_path
    recordingUrl: recording.recording_url,
    filename: filename,
    callAnswered: recording.call_answered,
    callEnd: recording.call_end,
    callType: recording.call_type,
    tickets: recording.tickets || [],
  };
}

// Convert camelCase to snake_case for API parameters
function toSnakeCase(str) {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

export async function searchRecordings(params) {
  console.log('[API] searchRecordings called with params:', params);
  const { apiUrl, instanceId } = getApiConfig();
  const queryParams = new URLSearchParams();

  // Always include instance_id
  queryParams.append('instance_id', instanceId);

  // Convert camelCase params to snake_case for backend API
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      const snakeKey = toSnakeCase(key);
      queryParams.append(snakeKey, value);
    }
  });

  const url = `${apiUrl}/api/recordings?${queryParams}`;
  const headers = await getHeaders();

  console.log('[API] Fetching:', url);
  console.log('[API] Headers:', Object.keys(headers));

  try {
    const response = await fetch(url, { headers });
    console.log('[API] Response status:', response.status);

    if (!response.ok) {
      throw new Error('Failed to fetch recordings');
    }

    const data = await response.json();
    console.log('[API] Received data:', {
      recordingsCount: data.recordings?.length,
      totalCount: data.total_count,
      page: data.page,
      totalPages: data.total_pages
    });

    // API returns snake_case, map to camelCase
    return {
      recordings: data.recordings.map(mapRecording),
      totalCount: data.total_count,
      page: data.page,
      pageSize: data.page_size,
      totalPages: data.total_pages
    };
  } catch (error) {
    console.error('[API] searchRecordings error:', error);
    throw error;
  }
}

export async function getRecordingUrl(blobPath, action = 'play') {
  // Call the API to get a presigned URL for the recording
  const { apiUrl, instanceId } = getApiConfig();
  const headers = await getHeaders();

  const url = `${apiUrl}/api/recordings/url?instance_id=${instanceId}&blob_path=${encodeURIComponent(blobPath)}&action=${action}`;
  console.log('[API] Getting recording URL:', url);

  try {
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`Failed to get recording URL: ${response.status}`);
    }
    const data = await response.json();
    console.log('[API] Got presigned URL');
    return data;
  } catch (error) {
    console.error('[API] getRecordingUrl error:', error);
    throw error;
  }
}

export async function getExtensions() {
  console.log('[API] getExtensions called');
  const { apiUrl, instanceId } = getApiConfig();
  const headers = await getHeaders();

  console.log('[API] Fetching extensions from:', `${apiUrl}/api/extensions`);

  try {
    const response = await fetch(`${apiUrl}/api/extensions?instance_id=${instanceId}`, { headers });
    console.log('[API] Extensions response status:', response.status);

    if (!response.ok) {
      console.error('[API] Extensions endpoint failed');
      return { extensions: [] };
    }

    const data = await response.json();
    console.log('[API] Received extensions:', data.extensions?.length || 0);
    return data;
  } catch (error) {
    console.error('[API] getExtensions error:', error);
    return { extensions: [] };
  }
}
